import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
import { cloneDeep } from "lodash";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_CMS, EMPTY_CMS_LIST_CONFIGURATION, EMPTY_CMS_LIST_CRITERIA } from "../constant/cmsConstant";
import { Cms } from "../objects/cms";
import { CmsListCriteria } from "../objects/cmsList";
import { CmsState } from "../objects/cmsState";
import { setCmsPreference } from "../utils/setCmsPreference";
import { Column } from "../objects/column";
import { Funnel } from "../objects/funnel";
import { EMPTY_FUNNEL_PRODUCTS_PARAMETER_LIST_CONFIGURATION, EMPTY_FUNNEL_PRODUCTS_PARAMETER_LIST_CRITERIA } from "../constant/funnelProductsParameterConstant";
import { setFunnelProductsParameterPreference } from "../utils/setFunnelProductsParameterPreference";
import { FunnelProductsParameter, FunnelProductsParameterListCriteria, FunnelProductsParameterProviders } from "../objects/funnelProductsParameter";
import { FunnelPriceDisplayRule, FunnelPriceDisplayRuleListCriteria } from "../objects/funnelPriceDisplayRule";
import { EMPTY_FUNNEL_PRICE_DISPLAY_RULE_LIST_CRITERIA, EMPTY_FUNNEL_PRICE_DISPLAY_RULE_LIST_CONFIGURATION } from "../constant/funnelPriceDisplayRuleConstant";
import { FunnelBookingEngineRule, FunnelBookingEngineRuleListCriteria } from "../objects/funnelBookingEngineRule";
import { EMPTY_FUNNEL_BOOKING_ENGINE_RULE_LIST_CRITERIA, EMPTY_FUNNEL_BOOKING_ENGINE_RULE_LIST_CONFIGURATION } from "../constant/funnelBookingEngineRuleConstant";
import { setFunnelPriceDisplayRulePreference } from "../utils/setFunnelPriceDisplayRulePreference";
import { setFunnelBookingEngineRulePreference } from "../utils/setFunnelBookingEngineRulePreference";
import { setFunnelBookingAuthorizationPreference } from "../utils/setFunnelBookingAuthorizationPreference";
import { FunnelBookingAuthorization, FunnelBookingAuthorizationListCriteria } from "../objects/funnelBookingAuthorization";
import { EMPTY_FUNNEL_BOOKING_AUTHORIZATION_LIST_CONFIGURATION, EMPTY_FUNNEL_BOOKING_AUTHORIZATION_LIST_CRITERIA } from "../constant/funnelBookingAuthorizationConstant";
import { FunnelMandatoryPaxInfoParameter, FunnelMandatoryPaxInfoParameterListCriteria } from "../objects/funnelMandatoryPaxInfoParameter";
import { EMPTY_FUNNEL_MANDATORY_PAX_INFO_PARAMETER_LIST_CONFIGURATION, EMPTY_FUNNEL_MANDATORY_PAX_INFO_PARAMETER_LIST_CRITERIA } from "../constant/funnelMandatoryPaxInfoParameterConstant";


const initialState: CmsState = {
    cms_list: [],
    cms_list_configuration: EMPTY_CMS_LIST_CONFIGURATION,
    cms_list_loading: false,
    cms_list_count: 0,
    cms_list_criteria: EMPTY_CMS_LIST_CRITERIA,
    cms: EMPTY_CMS,
    first_preference_loaded: false,
    delete_cms: null,
    funnel_list: [],
    funnel_count: 0,
    funnel: null,

    cms_allowed_destinations: [],
    funnel_allowed_destinations: [],

    cms_allowed_providers: [],
    funnel_allowed_providers: [],

    cms_allowed_clients: [],
    funnel_allowed_clients: [],

    funnel_products_parameter_list: [],
    funnel_products_parameter_list_configuration: EMPTY_FUNNEL_PRODUCTS_PARAMETER_LIST_CONFIGURATION,
    funnel_products_parameter_list_count: 0,
    funnel_products_parameter_list_loading: false,
    funnel_products_parameter_list_criteria: EMPTY_FUNNEL_PRODUCTS_PARAMETER_LIST_CRITERIA,
    funnel_products_parameter_refresh: false,
    funnel_products_parameter: null,
    funnel_products_parameter_provider_loading: false,
    funnel_products_parameter_providers: [],

    // price display rules
    funnel_price_display_rule_list: [],
    funnel_price_display_rule_list_configuration: EMPTY_FUNNEL_PRICE_DISPLAY_RULE_LIST_CONFIGURATION,
    funnel_price_display_rule_list_count: 0,
    funnel_price_display_rule_list_loading: false,
    funnel_price_display_rule_list_criteria: EMPTY_FUNNEL_PRICE_DISPLAY_RULE_LIST_CRITERIA,
    funnel_price_display_rule_refresh: false,
    funnel_price_display_rule: null,

    // booking engine rules
    funnel_booking_engine_rule_list: [],
    funnel_booking_engine_rule_list_configuration: EMPTY_FUNNEL_BOOKING_ENGINE_RULE_LIST_CONFIGURATION,
    funnel_booking_engine_rule_list_count: 0,
    funnel_booking_engine_rule_list_loading: false,
    funnel_booking_engine_rule_list_criteria: EMPTY_FUNNEL_BOOKING_ENGINE_RULE_LIST_CRITERIA,
    funnel_booking_engine_rule_refresh: false,
    funnel_booking_engine_rule: null,

    // booking authorization (F4)
    funnel_booking_authorization_list: [],
    funnel_booking_authorization_list_configuration: EMPTY_FUNNEL_BOOKING_AUTHORIZATION_LIST_CONFIGURATION,
    funnel_booking_authorization_list_count: 0,
    funnel_booking_authorization_list_loading: false,
    funnel_booking_authorization_list_criteria: EMPTY_FUNNEL_BOOKING_AUTHORIZATION_LIST_CRITERIA,
    funnel_booking_authorization_refresh: false,
    funnel_booking_authorization: null,

    // mandatory pax info parameters (F6)
    funnel_mandatory_pax_info_parameter_list: [],
    funnel_mandatory_pax_info_parameter_list_configuration: EMPTY_FUNNEL_MANDATORY_PAX_INFO_PARAMETER_LIST_CONFIGURATION,
    funnel_mandatory_pax_info_parameter_list_count: 0,
    funnel_mandatory_pax_info_parameter_list_loading: false,
    funnel_mandatory_pax_info_parameter_list_criteria: EMPTY_FUNNEL_MANDATORY_PAX_INFO_PARAMETER_LIST_CRITERIA,
    funnel_mandatory_pax_info_parameter_refresh: false,
    funnel_mandatory_pax_info_parameter: null,

};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const setDeleteCmsReducer = (state: CmsState, action: PayloadAction<Cms | null>): CmsState => ({
    ...state, delete_cms: action.payload
});

const addCmsListConfigurationPreferenceReducer = (state: CmsState, action: PayloadAction<Preferences[]>): CmsState => ({
    ...state, cms_list_configuration: { ...state.cms_list_configuration, preferences: [...state.cms_list_configuration.preferences, ...action.payload] }
});

const setCmsListConfigurationPreferenceReducer = (state: CmsState, action: PayloadAction<Preferences[]>): CmsState => ({
    ...state, cms_list_configuration: { ...state.cms_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: CmsState, action: PayloadAction<Preferences[]>): CmsState => {
    const { preferences, columns, order_by, order } = setCmsPreference(action.payload, state.cms_list_configuration.columns);
    const funnel_products_parameter_preferences = setFunnelProductsParameterPreference(action.payload, state.funnel_products_parameter_list_configuration.columns);
    const funnel_price_display_rule_preferences = setFunnelPriceDisplayRulePreference(action.payload, state.funnel_price_display_rule_list_configuration.columns);
    const funnel_booking_engine_rule_preferences = setFunnelBookingEngineRulePreference(action.payload, state.funnel_booking_engine_rule_list_configuration.columns);
    const funnel_booking_authorization_preferences = setFunnelBookingAuthorizationPreference(action.payload, state.funnel_booking_authorization_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        cms_list_configuration: { ...state.cms_list_configuration, preferences, columns },
        cms_list_criteria: { ...state.cms_list_criteria, order, order_by },
        funnel_products_parameter_list_configuration: { ...state.funnel_products_parameter_list_configuration, preferences: funnel_products_parameter_preferences.preferences, columns: funnel_products_parameter_preferences.columns },
        funnel_products_parameter_list_criteria: { ...state.funnel_products_parameter_list_criteria, order: funnel_products_parameter_preferences.order, order_by: funnel_products_parameter_preferences.order_by },
        funnel_price_display_rule_list_configuration: { ...state.funnel_price_display_rule_list_configuration, preferences: funnel_price_display_rule_preferences.preferences, columns: funnel_price_display_rule_preferences.columns },
        funnel_price_display_rule_list_criteria: { ...state.funnel_price_display_rule_list_criteria, order: funnel_price_display_rule_preferences.order, order_by: funnel_price_display_rule_preferences.order_by },
        funnel_booking_engine_rule_list_configuration: { ...state.funnel_booking_engine_rule_list_configuration, preferences: funnel_booking_engine_rule_preferences.preferences, columns: funnel_booking_engine_rule_preferences.columns },
        funnel_booking_engine_rule_list_criteria: { ...state.funnel_booking_engine_rule_list_criteria, order: funnel_booking_engine_rule_preferences.order, order_by: funnel_booking_engine_rule_preferences.order_by },
        funnel_booking_authorization_list_configuration: { ...state.funnel_booking_authorization_list_configuration, preferences: funnel_booking_authorization_preferences.preferences, columns: funnel_booking_authorization_preferences.columns },
        funnel_booking_authorization_list_criteria: { ...state.funnel_booking_authorization_list_criteria, order: funnel_booking_authorization_preferences.order, order_by: funnel_booking_authorization_preferences.order_by },
    };
};

const toggleColumnReducer = (state: CmsState, action: PayloadAction<number>): CmsState => {
    const columns = cloneDeep([...state.cms_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, cms_list_configuration: { ...state.cms_list_configuration, columns } };
};

const updateCmsListReducer = (state: CmsState, action: PayloadAction<Cms[]>): CmsState => ({
    ...state, cms_list: action.payload
});
const updateCmsListCriteriaReducer = (state: CmsState, action: PayloadAction<CmsListCriteria>): CmsState => ({
    ...state, cms_list_criteria: action.payload
});
const updateCmsReducer = (state: CmsState, action: PayloadAction<Cms>): CmsState => ({
    ...state, cms: action.payload
});
const updateCmsListLoadingReducer = (state: CmsState, action: PayloadAction<boolean>): CmsState => ({
    ...state, cms_list_loading: action.payload
});
const updateCmsListCountReducer = (state: CmsState, action: PayloadAction<number>): CmsState => ({
    ...state, cms_list_count: action.payload
});

const setColumnsReducer = (state: CmsState, action: PayloadAction<Column[]>): CmsState => ({
    ...state, cms_list_configuration: { ...state.cms_list_configuration, columns: action.payload },
});

const updateFunnelListReducer = (state: CmsState, action: PayloadAction<Funnel[]>): CmsState => ({
    ...state, funnel_list: action.payload
});
const updateFunnelListElementReducer = (state: CmsState, action: PayloadAction<Funnel>): CmsState => ({
    ...state, funnel_list: state.funnel_list.map(funnel => funnel.id === action.payload.id ? action.payload : funnel)
});
const updateFunnelListCountReducer = (state: CmsState, action: PayloadAction<number>): CmsState => ({
    ...state, funnel_count: action.payload
});
const updateFunnelReducer = (state: CmsState, action: PayloadAction<Funnel | null>): CmsState => ({
    ...state, funnel: action.payload
});


// destinations
const updateFunnelAllowedDestinationsReducer = (state: CmsState, action: PayloadAction<any[]>): CmsState => ({
    ...state, funnel_allowed_destinations: action.payload
});
const updateCmsAllowedDestinationsReducer = (state: CmsState, action: PayloadAction<any[]>): CmsState => ({
    ...state, cms_allowed_destinations: action.payload
});

// providers
const updateFunnelAllowedProvidersReducer = (state: CmsState, action: PayloadAction<any[]>): CmsState => ({
    ...state, funnel_allowed_providers: action.payload
});
const updateCmsAllowedProvidersReducer = (state: CmsState, action: PayloadAction<any[]>): CmsState => ({
    ...state, cms_allowed_providers: action.payload
});

// clients
const updateFunnelAllowedClientsReducer = (state: CmsState, action: PayloadAction<any[]>): CmsState => ({
    ...state, funnel_allowed_clients: action.payload
});
const updateCmsAllowedClientsReducer = (state: CmsState, action: PayloadAction<any[]>): CmsState => ({
    ...state, cms_allowed_clients: action.payload
});



// funnel provider edit
const addFunnelProductsParameterListConfigurationPreferenceReducer = (state: CmsState, action: PayloadAction<Preferences[]>): CmsState => ({
    ...state, funnel_products_parameter_list_configuration: { ...state.funnel_products_parameter_list_configuration, preferences: [...state.funnel_products_parameter_list_configuration.preferences, ...action.payload] }
});

const setFunnelProductsParameterListConfigurationPreferenceReducer = (state: CmsState, action: PayloadAction<Preferences[]>): CmsState => ({
    ...state, funnel_products_parameter_list_configuration: { ...state.funnel_products_parameter_list_configuration, preferences: action.payload }
});
const setFunnelProductsParameterColumnsReducer = (state: CmsState, action: PayloadAction<Column[]>): CmsState => ({
    ...state, funnel_products_parameter_list_configuration: { ...state.funnel_products_parameter_list_configuration, columns: action.payload },
});

const toggleFunnelProductsParameterColumnReducer = (state: CmsState, action: PayloadAction<number>): CmsState => {
    const columns = cloneDeep([...state.funnel_products_parameter_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, funnel_products_parameter_list_configuration: { ...state.funnel_products_parameter_list_configuration, columns } };
};

const updateFunnelProductsParameterListReducer = (state: CmsState, action: PayloadAction<FunnelProductsParameter[]>): CmsState => ({
    ...state, funnel_products_parameter_list: action.payload || []
});
const updateFunnelProductsParameterListCriteriaReducer = (state: CmsState, action: PayloadAction<FunnelProductsParameterListCriteria>): CmsState => ({
    ...state, funnel_products_parameter_list_criteria: action.payload
});
const updateFunnelProductsParameterReducer = (state: CmsState, action: PayloadAction<FunnelProductsParameter | null>): CmsState => ({
    ...state,
    funnel_products_parameter: action.payload,
    // getFunnelProductsParameterData(response.data.results[0], Number(current_locale))
});
const updateFunnelProductsParameterListLoadingReducer = (state: CmsState, action: PayloadAction<boolean>): CmsState => ({
    ...state, funnel_products_parameter_list_loading: action.payload
});
const updateFunnelProductsParameterListCountReducer = (state: CmsState, action: PayloadAction<number>): CmsState => ({
    ...state, funnel_products_parameter_list_count: action.payload
});
const toggleFunnelProductsParameterRefreshReducer = (state: CmsState): CmsState => ({
    ...state, funnel_products_parameter_refresh: !state.funnel_products_parameter_refresh
});
const updateFunnelProductsParameterProviderLoadingReducer = (state: CmsState, action: PayloadAction<boolean>): CmsState => ({
    ...state, funnel_products_parameter_provider_loading: action.payload
});
const updateFunnelProductsParameterProvidersReducer = (state: CmsState, action: PayloadAction<FunnelProductsParameterProviders[]>): CmsState => ({
    ...state, funnel_products_parameter_providers: action.payload
});

// price display rules
const updateFunnelPriceDisplayRuleListReducer = (state: CmsState, action: PayloadAction<FunnelPriceDisplayRule[]>): CmsState => ({
    ...state, funnel_price_display_rule_list: action.payload || []
});
const updateFunnelPriceDisplayRuleListCountReducer = (state: CmsState, action: PayloadAction<number>): CmsState => ({
    ...state, funnel_price_display_rule_list_count: action.payload
});
const updateFunnelPriceDisplayRuleListLoadingReducer = (state: CmsState, action: PayloadAction<boolean>): CmsState => ({
    ...state, funnel_price_display_rule_list_loading: action.payload
});
const updateFunnelPriceDisplayRuleListCriteriaReducer = (state: CmsState, action: PayloadAction<FunnelPriceDisplayRuleListCriteria>): CmsState => ({
    ...state, funnel_price_display_rule_list_criteria: action.payload
});
const toggleFunnelPriceDisplayRuleRefreshReducer = (state: CmsState): CmsState => ({
    ...state, funnel_price_display_rule_refresh: !state.funnel_price_display_rule_refresh
});
const updateFunnelPriceDisplayRuleReducer = (state: CmsState, action: PayloadAction<FunnelPriceDisplayRule | null>): CmsState => ({
    ...state, funnel_price_display_rule: action.payload
});
const addFunnelPriceDisplayRuleListConfigurationPreferenceReducer = (state: CmsState, action: PayloadAction<Preferences[]>): CmsState => ({
    ...state, funnel_price_display_rule_list_configuration: { ...state.funnel_price_display_rule_list_configuration, preferences: [...state.funnel_price_display_rule_list_configuration.preferences, ...action.payload] }
});
const setFunnelPriceDisplayRuleListConfigurationPreferenceReducer = (state: CmsState, action: PayloadAction<Preferences[]>): CmsState => ({
    ...state, funnel_price_display_rule_list_configuration: { ...state.funnel_price_display_rule_list_configuration, preferences: action.payload }
});
const setFunnelPriceDisplayRuleColumnsReducer = (state: CmsState, action: PayloadAction<Column[]>): CmsState => ({
    ...state, funnel_price_display_rule_list_configuration: { ...state.funnel_price_display_rule_list_configuration, columns: action.payload }
});
const toggleFunnelPriceDisplayRuleColumnReducer = (state: CmsState, action: PayloadAction<number>): CmsState => {
    const columns = cloneDeep([...state.funnel_price_display_rule_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, funnel_price_display_rule_list_configuration: { ...state.funnel_price_display_rule_list_configuration, columns } };
};

// booking engine rules
const updateFunnelBookingEngineRuleListReducer = (state: CmsState, action: PayloadAction<FunnelBookingEngineRule[]>): CmsState => ({
    ...state, funnel_booking_engine_rule_list: action.payload || []
});
const updateFunnelBookingEngineRuleListCountReducer = (state: CmsState, action: PayloadAction<number>): CmsState => ({
    ...state, funnel_booking_engine_rule_list_count: action.payload
});
const updateFunnelBookingEngineRuleListLoadingReducer = (state: CmsState, action: PayloadAction<boolean>): CmsState => ({
    ...state, funnel_booking_engine_rule_list_loading: action.payload
});
const updateFunnelBookingEngineRuleListCriteriaReducer = (state: CmsState, action: PayloadAction<FunnelBookingEngineRuleListCriteria>): CmsState => ({
    ...state, funnel_booking_engine_rule_list_criteria: action.payload
});
const toggleFunnelBookingEngineRuleRefreshReducer = (state: CmsState): CmsState => ({
    ...state, funnel_booking_engine_rule_refresh: !state.funnel_booking_engine_rule_refresh
});
const updateFunnelBookingEngineRuleReducer = (state: CmsState, action: PayloadAction<FunnelBookingEngineRule | null>): CmsState => ({
    ...state, funnel_booking_engine_rule: action.payload
});
const addFunnelBookingEngineRuleListConfigurationPreferenceReducer = (state: CmsState, action: PayloadAction<Preferences[]>): CmsState => ({
    ...state, funnel_booking_engine_rule_list_configuration: { ...state.funnel_booking_engine_rule_list_configuration, preferences: [...state.funnel_booking_engine_rule_list_configuration.preferences, ...action.payload] }
});
const setFunnelBookingEngineRuleListConfigurationPreferenceReducer = (state: CmsState, action: PayloadAction<Preferences[]>): CmsState => ({
    ...state, funnel_booking_engine_rule_list_configuration: { ...state.funnel_booking_engine_rule_list_configuration, preferences: action.payload }
});
const setFunnelBookingEngineRuleColumnsReducer = (state: CmsState, action: PayloadAction<Column[]>): CmsState => ({
    ...state, funnel_booking_engine_rule_list_configuration: { ...state.funnel_booking_engine_rule_list_configuration, columns: action.payload }
});
const toggleFunnelBookingEngineRuleColumnReducer = (state: CmsState, action: PayloadAction<number>): CmsState => {
    const columns = cloneDeep([...state.funnel_booking_engine_rule_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, funnel_booking_engine_rule_list_configuration: { ...state.funnel_booking_engine_rule_list_configuration, columns } };
};

// booking authorization (F4)
const addFunnelBookingAuthorizationListConfigurationPreferenceReducer = (state: CmsState, action: PayloadAction<Preferences[]>): CmsState => ({
    ...state, funnel_booking_authorization_list_configuration: { ...state.funnel_booking_authorization_list_configuration, preferences: [...state.funnel_booking_authorization_list_configuration.preferences, ...action.payload] }
});
const setFunnelBookingAuthorizationListConfigurationPreferenceReducer = (state: CmsState, action: PayloadAction<Preferences[]>): CmsState => ({
    ...state, funnel_booking_authorization_list_configuration: { ...state.funnel_booking_authorization_list_configuration, preferences: action.payload }
});
const setFunnelBookingAuthorizationColumnsReducer = (state: CmsState, action: PayloadAction<Column[]>): CmsState => ({
    ...state, funnel_booking_authorization_list_configuration: { ...state.funnel_booking_authorization_list_configuration, columns: action.payload }
});
const toggleFunnelBookingAuthorizationColumnReducer = (state: CmsState, action: PayloadAction<number>): CmsState => {
    const columns = cloneDeep([...state.funnel_booking_authorization_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, funnel_booking_authorization_list_configuration: { ...state.funnel_booking_authorization_list_configuration, columns } };
};
const updateFunnelBookingAuthorizationListReducer = (state: CmsState, action: PayloadAction<FunnelBookingAuthorization[]>): CmsState => ({
    ...state, funnel_booking_authorization_list: action.payload || []
});
const updateFunnelBookingAuthorizationListCountReducer = (state: CmsState, action: PayloadAction<number>): CmsState => ({
    ...state, funnel_booking_authorization_list_count: action.payload
});
const updateFunnelBookingAuthorizationListLoadingReducer = (state: CmsState, action: PayloadAction<boolean>): CmsState => ({
    ...state, funnel_booking_authorization_list_loading: action.payload
});
const updateFunnelBookingAuthorizationListCriteriaReducer = (state: CmsState, action: PayloadAction<FunnelBookingAuthorizationListCriteria>): CmsState => ({
    ...state, funnel_booking_authorization_list_criteria: action.payload
});
const toggleFunnelBookingAuthorizationRefreshReducer = (state: CmsState): CmsState => ({
    ...state, funnel_booking_authorization_refresh: !state.funnel_booking_authorization_refresh
});
const updateFunnelBookingAuthorizationReducer = (state: CmsState, action: PayloadAction<FunnelBookingAuthorization | null>): CmsState => ({
    ...state, funnel_booking_authorization: action.payload
});

// mandatory pax info parameters (F6)
const updateFunnelMandatoryPaxInfoParameterListReducer = (state: CmsState, action: PayloadAction<FunnelMandatoryPaxInfoParameter[]>): CmsState => ({
    ...state, funnel_mandatory_pax_info_parameter_list: action.payload || []
});
const updateFunnelMandatoryPaxInfoParameterListCountReducer = (state: CmsState, action: PayloadAction<number>): CmsState => ({
    ...state, funnel_mandatory_pax_info_parameter_list_count: action.payload
});
const updateFunnelMandatoryPaxInfoParameterListLoadingReducer = (state: CmsState, action: PayloadAction<boolean>): CmsState => ({
    ...state, funnel_mandatory_pax_info_parameter_list_loading: action.payload
});
const updateFunnelMandatoryPaxInfoParameterListCriteriaReducer = (state: CmsState, action: PayloadAction<FunnelMandatoryPaxInfoParameterListCriteria>): CmsState => ({
    ...state, funnel_mandatory_pax_info_parameter_list_criteria: action.payload
});
const toggleFunnelMandatoryPaxInfoParameterRefreshReducer = (state: CmsState): CmsState => ({
    ...state, funnel_mandatory_pax_info_parameter_refresh: !state.funnel_mandatory_pax_info_parameter_refresh
});
const updateFunnelMandatoryPaxInfoParameterReducer = (state: CmsState, action: PayloadAction<FunnelMandatoryPaxInfoParameter | null>): CmsState => ({
    ...state, funnel_mandatory_pax_info_parameter: action.payload
});

export const cms = createSlice({
    name: 'cms',
    initialState,
    reducers: {
        setDeleteCms: setDeleteCmsReducer,
        addCmsListConfigurationPreference: addCmsListConfigurationPreferenceReducer,
        setCmsListConfigurationPreference: setCmsListConfigurationPreferenceReducer,
        toggleColumn: toggleColumnReducer,
        updateCmsList: updateCmsListReducer,
        updateCmsListCriteria: updateCmsListCriteriaReducer,
        updateCms: updateCmsReducer,
        updateCmsListLoading: updateCmsListLoadingReducer,
        updateCmsListCount: updateCmsListCountReducer,
        setColumns: setColumnsReducer,
        updateFunnelList: updateFunnelListReducer,
        updateFunnelListElement: updateFunnelListElementReducer,
        updateFunnelListCount: updateFunnelListCountReducer,
        updateFunnel: updateFunnelReducer,

        updateFunnelAllowedDestinations: updateFunnelAllowedDestinationsReducer,
        updateCmsAllowedDestinations: updateCmsAllowedDestinationsReducer,

        updateFunnelAllowedProviders: updateFunnelAllowedProvidersReducer,
        updateCmsAllowedProviders: updateCmsAllowedProvidersReducer,

        updateFunnelAllowedClients: updateFunnelAllowedClientsReducer,
        updateCmsAllowedClients: updateCmsAllowedClientsReducer,

        // funnel products parameter
        addFunnelProductsParameterListConfigurationPreference: addFunnelProductsParameterListConfigurationPreferenceReducer,
        setFunnelProductsParameterListConfigurationPreference: setFunnelProductsParameterListConfigurationPreferenceReducer,
        updateFunnelProductsParameterListCount: updateFunnelProductsParameterListCountReducer,
        setFunnelProductsParameterColumns: setFunnelProductsParameterColumnsReducer,
        toggleFunnelProductsParameterColumn: toggleFunnelProductsParameterColumnReducer,
        updateFunnelProductsParameterList: updateFunnelProductsParameterListReducer,
        updateFunnelProductsParameterListCriteria: updateFunnelProductsParameterListCriteriaReducer,
        updateFunnelProductsParameter: updateFunnelProductsParameterReducer,
        updateFunnelProductsParameterListLoading: updateFunnelProductsParameterListLoadingReducer,
        toggleFunnelProductsParameterRefresh: toggleFunnelProductsParameterRefreshReducer,
        updateFunnelProductsParameterProviderLoading: updateFunnelProductsParameterProviderLoadingReducer,
        updateFunnelProductsParameterProviders: updateFunnelProductsParameterProvidersReducer,

        // price display rules
        updateFunnelPriceDisplayRuleList: updateFunnelPriceDisplayRuleListReducer,
        updateFunnelPriceDisplayRuleListCount: updateFunnelPriceDisplayRuleListCountReducer,
        updateFunnelPriceDisplayRuleListLoading: updateFunnelPriceDisplayRuleListLoadingReducer,
        updateFunnelPriceDisplayRuleListCriteria: updateFunnelPriceDisplayRuleListCriteriaReducer,
        toggleFunnelPriceDisplayRuleRefresh: toggleFunnelPriceDisplayRuleRefreshReducer,
        updateFunnelPriceDisplayRule: updateFunnelPriceDisplayRuleReducer,
        addFunnelPriceDisplayRuleListConfigurationPreference: addFunnelPriceDisplayRuleListConfigurationPreferenceReducer,
        setFunnelPriceDisplayRuleListConfigurationPreference: setFunnelPriceDisplayRuleListConfigurationPreferenceReducer,
        setFunnelPriceDisplayRuleColumns: setFunnelPriceDisplayRuleColumnsReducer,
        toggleFunnelPriceDisplayRuleColumn: toggleFunnelPriceDisplayRuleColumnReducer,

        // booking engine rules
        updateFunnelBookingEngineRuleList: updateFunnelBookingEngineRuleListReducer,
        updateFunnelBookingEngineRuleListCount: updateFunnelBookingEngineRuleListCountReducer,
        updateFunnelBookingEngineRuleListLoading: updateFunnelBookingEngineRuleListLoadingReducer,
        updateFunnelBookingEngineRuleListCriteria: updateFunnelBookingEngineRuleListCriteriaReducer,
        toggleFunnelBookingEngineRuleRefresh: toggleFunnelBookingEngineRuleRefreshReducer,
        updateFunnelBookingEngineRule: updateFunnelBookingEngineRuleReducer,
        addFunnelBookingEngineRuleListConfigurationPreference: addFunnelBookingEngineRuleListConfigurationPreferenceReducer,
        setFunnelBookingEngineRuleListConfigurationPreference: setFunnelBookingEngineRuleListConfigurationPreferenceReducer,
        setFunnelBookingEngineRuleColumns: setFunnelBookingEngineRuleColumnsReducer,
        toggleFunnelBookingEngineRuleColumn: toggleFunnelBookingEngineRuleColumnReducer,

        // booking authorization (F4)
        updateFunnelBookingAuthorizationList: updateFunnelBookingAuthorizationListReducer,
        updateFunnelBookingAuthorizationListCount: updateFunnelBookingAuthorizationListCountReducer,
        updateFunnelBookingAuthorizationListLoading: updateFunnelBookingAuthorizationListLoadingReducer,
        updateFunnelBookingAuthorizationListCriteria: updateFunnelBookingAuthorizationListCriteriaReducer,
        toggleFunnelBookingAuthorizationRefresh: toggleFunnelBookingAuthorizationRefreshReducer,
        updateFunnelBookingAuthorization: updateFunnelBookingAuthorizationReducer,
        addFunnelBookingAuthorizationListConfigurationPreference: addFunnelBookingAuthorizationListConfigurationPreferenceReducer,
        setFunnelBookingAuthorizationListConfigurationPreference: setFunnelBookingAuthorizationListConfigurationPreferenceReducer,
        setFunnelBookingAuthorizationColumns: setFunnelBookingAuthorizationColumnsReducer,
        toggleFunnelBookingAuthorizationColumn: toggleFunnelBookingAuthorizationColumnReducer,

        // mandatory pax info parameters (F6)
        updateFunnelMandatoryPaxInfoParameterList: updateFunnelMandatoryPaxInfoParameterListReducer,
        updateFunnelMandatoryPaxInfoParameterListCount: updateFunnelMandatoryPaxInfoParameterListCountReducer,
        updateFunnelMandatoryPaxInfoParameterListLoading: updateFunnelMandatoryPaxInfoParameterListLoadingReducer,
        updateFunnelMandatoryPaxInfoParameterListCriteria: updateFunnelMandatoryPaxInfoParameterListCriteriaReducer,
        toggleFunnelMandatoryPaxInfoParameterRefresh: toggleFunnelMandatoryPaxInfoParameterRefreshReducer,
        updateFunnelMandatoryPaxInfoParameter: updateFunnelMandatoryPaxInfoParameterReducer,

        // column resize
        resizeCmsColumn(state, action: PayloadAction<ColumnResizePayload>) {
            state.cms_list_configuration.columns = applyColumnResize(state.cms_list_configuration.columns, action.payload);
        },
        resizeFunnelProductsParameterColumn(state, action: PayloadAction<ColumnResizePayload>) {
            state.funnel_products_parameter_list_configuration.columns = applyColumnResize(state.funnel_products_parameter_list_configuration.columns, action.payload);
        },
        resizeFunnelPriceDisplayRuleColumn(state, action: PayloadAction<ColumnResizePayload>) {
            state.funnel_price_display_rule_list_configuration.columns = applyColumnResize(state.funnel_price_display_rule_list_configuration.columns, action.payload);
        },
        resizeFunnelBookingEngineRuleColumn(state, action: PayloadAction<ColumnResizePayload>) {
            state.funnel_booking_engine_rule_list_configuration.columns = applyColumnResize(state.funnel_booking_engine_rule_list_configuration.columns, action.payload);
        },
        resizeFunnelBookingAuthorizationColumn(state, action: PayloadAction<ColumnResizePayload>) {
            state.funnel_booking_authorization_list_configuration.columns = applyColumnResize(state.funnel_booking_authorization_list_configuration.columns, action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    setDeleteCms,
    addCmsListConfigurationPreference,
    setCmsListConfigurationPreference,
    setColumns,
    toggleColumn,
    updateCmsList,
    updateCmsListCriteria,
    updateCms,
    updateCmsListLoading,
    updateCmsListCount,
    updateFunnelList,
    updateFunnelListElement,
    updateFunnelListCount,
    updateFunnel,
    updateFunnelAllowedDestinations,
    updateCmsAllowedDestinations,
    updateFunnelAllowedProviders,
    updateCmsAllowedProviders,
    updateFunnelAllowedClients,
    updateCmsAllowedClients,
    addFunnelProductsParameterListConfigurationPreference,
    setFunnelProductsParameterListConfigurationPreference,
    updateFunnelProductsParameterListCount,
    setFunnelProductsParameterColumns,
    toggleFunnelProductsParameterColumn,
    updateFunnelProductsParameterList,
    updateFunnelProductsParameterListCriteria,
    updateFunnelProductsParameter,
    updateFunnelProductsParameterListLoading,
    toggleFunnelProductsParameterRefresh,
    updateFunnelProductsParameterProviderLoading,
    updateFunnelProductsParameterProviders,
    updateFunnelPriceDisplayRuleList,
    updateFunnelPriceDisplayRuleListCount,
    updateFunnelPriceDisplayRuleListLoading,
    updateFunnelPriceDisplayRuleListCriteria,
    toggleFunnelPriceDisplayRuleRefresh,
    updateFunnelPriceDisplayRule,
    addFunnelPriceDisplayRuleListConfigurationPreference,
    setFunnelPriceDisplayRuleListConfigurationPreference,
    setFunnelPriceDisplayRuleColumns,
    toggleFunnelPriceDisplayRuleColumn,
    updateFunnelBookingEngineRuleList,
    updateFunnelBookingEngineRuleListCount,
    updateFunnelBookingEngineRuleListLoading,
    updateFunnelBookingEngineRuleListCriteria,
    toggleFunnelBookingEngineRuleRefresh,
    updateFunnelBookingEngineRule,
    addFunnelBookingEngineRuleListConfigurationPreference,
    setFunnelBookingEngineRuleListConfigurationPreference,
    setFunnelBookingEngineRuleColumns,
    toggleFunnelBookingEngineRuleColumn,
    updateFunnelBookingAuthorizationList,
    updateFunnelBookingAuthorizationListCount,
    updateFunnelBookingAuthorizationListLoading,
    updateFunnelBookingAuthorizationListCriteria,
    toggleFunnelBookingAuthorizationRefresh,
    updateFunnelBookingAuthorization,
    addFunnelBookingAuthorizationListConfigurationPreference,
    setFunnelBookingAuthorizationListConfigurationPreference,
    setFunnelBookingAuthorizationColumns,
    toggleFunnelBookingAuthorizationColumn,
    updateFunnelMandatoryPaxInfoParameterList,
    updateFunnelMandatoryPaxInfoParameterListCount,
    updateFunnelMandatoryPaxInfoParameterListLoading,
    updateFunnelMandatoryPaxInfoParameterListCriteria,
    toggleFunnelMandatoryPaxInfoParameterRefresh,
    updateFunnelMandatoryPaxInfoParameter,
    resizeCmsColumn,
    resizeFunnelProductsParameterColumn,
    resizeFunnelPriceDisplayRuleColumn,
    resizeFunnelBookingEngineRuleColumn,
    resizeFunnelBookingAuthorizationColumn,
} = cms.actions;